import {
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { createIdMap } from "@tellescope/utils";
import { useCallback, useMemo } from "react";

export interface UseInboxScheduledArgs {
  enabled?: boolean;
}

export type InboxScheduledItem =
  | (TellescopeModelFromName<"emails"> & { channel: "Email" })
  | (TellescopeModelFromName<"sms_messages"> & { channel: "SMS" })
  | (TellescopeModelFromName<"chats"> & { channel: "Chat" });

export const useInboxScheduledMessages = ({ enabled = true }: UseInboxScheduledArgs = {}) => {
  const smsMessagesQuery = useTellescopeInfiniteQuery({
    model: "sms_messages",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
        },
        delivered: false,
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"sms_messages"> & { channel: "SMS" }>((item) => ({ ...item, channel: "SMS" })),
    enabled,
  });
  const emailsQuery = useTellescopeInfiniteQuery({
    model: "emails",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
        },
        delivered: false,
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"emails"> & { channel: "Email" }>((item) => ({ ...item, channel: "Email" })),
    enabled,
  });
  const chatsQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
          $gt: new Date(1).toISOString(),
        },
        timestamp: {
          $exists: false,
        },
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"chats"> & { channel: "Chat" }>((item) => ({ ...item, channel: "Chat" })),
    enabled,
  });

  const isLoadingMessages = smsMessagesQuery.isLoading || emailsQuery.isLoading || chatsQuery.isLoading;
  const messagesIsError = smsMessagesQuery.isError || emailsQuery.isError || chatsQuery.isError;

  const { messages, enduserIds, chatroomIds } = useMemo<{
    messages: InboxScheduledItem[];
    enduserIds: string[];
    chatroomIds: string[];
  }>(() => {
    if (isLoadingMessages) {
      // short circuit to not prematurely display a mis-ordered list of drafts when some are still loading
      return {
        messages: [],
        enduserIds: [],
        chatroomIds: [],
      };
    }

    const enduserIds = new Set<string>();
    const chatroomIds = new Set<string>();
    const messages = [...(smsMessagesQuery.data ?? []), ...(emailsQuery.data ?? []), ...(chatsQuery.data ?? [])].sort(
      (a, b) => {
        if (a.enduserId) {
          enduserIds.add(a.enduserId);
        }
        if (a.channel === "Chat") {
          chatroomIds.add(a.roomId);
        }

        return b.updatedAt.getTime() - a.updatedAt.getTime();
      }
    );

    // Handle cases where `.sort` is not called because combinedDrafts.length === 1
    const onlyMessage = messages.length === 1 ? messages[0] : undefined;
    const onlyEnduserId = onlyMessage ? onlyMessage.enduserId : undefined;
    const onlyChatroomId = onlyMessage && onlyMessage.channel === "Chat" ? onlyMessage.roomId : undefined;

    return {
      messages,
      enduserIds: onlyEnduserId ? [onlyEnduserId] : Array.from(enduserIds),
      chatroomIds: onlyChatroomId ? [onlyChatroomId] : Array.from(chatroomIds),
    };
  }, [chatsQuery.data, emailsQuery.data, isLoadingMessages, smsMessagesQuery.data]);

  const endusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: enduserIds,
    },
    enabled: !!enduserIds.length,
    select: (data) => ({
      endusers: data.matches,
      endusersMap: createIdMap(data.matches),
    }),
  });

  const getEndusers = useCallback(
    (ids?: string[] | string) => {
      const normalizedIds = ids === undefined ? [] : typeof ids === "string" ? [ids] : ids;
      return normalizedIds.reduce<TellescopeModelFromName<"endusers">[]>((acc, curr) => {
        const enduser = endusersQuery.data?.endusersMap[curr];
        if (enduser) {
          acc.push(enduser);
        }
        return acc;
      }, []);
    },
    [endusersQuery.data?.endusersMap]
  );

  const chatroomsQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getByIds",
    request: { ids: chatroomIds },
    select: (data) => ({
      chatrooms: data.matches,
      chatroomsMap: createIdMap(data.matches),
    }),
    enabled: !!chatroomIds.length,
  });

  return {
    messages,
    isLoadingMessages,
    messagesIsError,
    endusers: endusersQuery.data,
    isLoadingEndusers: endusersQuery.isLoading,
    endusersIsError: endusersQuery.isError,
    chatrooms: chatroomsQuery.data,
    chatroomsIsLoading: chatroomsQuery.isLoading,
    chatroomsIsError: chatroomsQuery.isError,
    isLoading: isLoadingMessages || endusersQuery.isLoading || chatroomsQuery.isLoading,
    isError: messagesIsError || endusersQuery.isError || chatroomsQuery.isError,
    getEndusers,
  };
};
